// Typed accessors over the per-service stores, plus small domain helpers used by several routes.
import { readStore, writeStore } from "./db";
import {
  TENANTS, SEED_USERS, SEED_PROFILES, SEED_BOTS, SEED_CONVERSATIONS, SEED_LEADS, SEED_KNOWLEDGE,
  TEAM_ROLE_TO_ROLE, makeBot
} from "./fixtures";
import { genId, relativeTime } from "./helpers";

const collection = (name, seed) => ({
  all: () => readStore(name, seed),
  save: (items) => writeStore(name, items)
});

export const usersStore = collection("users", SEED_USERS);
export const tenantsStore = collection("tenants", TENANTS);
export const botsStore = collection("bots", SEED_BOTS);
export const knowledgeStore = collection("knowledge", SEED_KNOWLEDGE);
export const importsStore = collection("imports", []);
export const conversationsStore = collection("conversations", SEED_CONVERSATIONS);
export const leadsStore = collection("leads", SEED_LEADS);

// Keyed objects: { [key]: value }
export const keyed = (name, seed) => ({
  all: () => readStore(name, seed),
  save: (obj) => writeStore(name, obj)
});
export const sessionsStore = keyed("sessions", {});
export const codesStore = keyed("codes", {});
export const profilesStore = keyed("profiles", SEED_PROFILES);
export const domainsStore = keyed("domains", {
  "tenant-acme": [
    { id: "dom-1", domain: "acme.com", addedAt: "2026-08-01T10:00:00Z" },
    { id: "dom-2", domain: "shop.acme.com", addedAt: "2026-08-15T11:30:00Z" }
  ],
  "tenant-globex": [{ id: "dom-3", domain: "globex.com", addedAt: "2026-07-20T09:00:00Z" }]
});
export const marketingStore = keyed("marketing", {});
export const settingsStore = keyed("settings", {});
export const tenantKeysStore = keyed("tenantKeys", {});
export const webhooksStore = keyed("webhooks", {});
export const aiUsageStore = keyed("aiUsage", {});

// ------------------------------------------------------------------------------------------
// Subscription limits (USE-01). The Python backend owns the real plan table.
export const BOT_LIMITS = { Starter: 1, Business: 3, Enterprise: 10 };
export const botLimitFor = (tenantId) => BOT_LIMITS[(tenantsStore.all().find((t) => t.id === tenantId) || {}).plan] || 1;
export const planFor = (tenantId) => (tenantsStore.all().find((t) => t.id === tenantId) || {}).plan || "Starter";

export const roleIdForUser = (user) => TEAM_ROLE_TO_ROLE[user.teamRole] || "viewer";

/** What the API may expose about an account — never the password or a pending invite code. */
export function publicUser(user) {
  if (!user) return null;
  const { password, inviteCode, verified, ...rest } = user; // eslint-disable-line no-unused-vars
  return { ...rest, role: user.teamRole, roleId: roleIdForUser(user) };
}

export const teamMemberView = (user, { includeInvite = false } = {}) => ({
  id: user.id, name: user.name, email: user.email, role: user.teamRole, status: user.status,
  lastActive: user.lastActive || "—", ...(includeInvite && user.status === "Invited" ? { inviteCode: user.inviteCode } : {})
});

export function tenantName(tenantId) {
  return tenantsStore.all().find((t) => t.id === tenantId)?.name || "";
}

export function botView(bot) {
  return { ...bot, updated: relativeTime(bot.updatedAt) };
}

export const LEAD_CAPTURE_DEFAULTS = { phone: "optional", company: false, message: false };
/** Lead-capture form settings with defaults filled in (older bots have none saved). */
export const leadCaptureFor = (bot) => ({ ...LEAD_CAPTURE_DEFAULTS, ...((bot && bot.config && bot.config.leadCapture) || {}) });

/** Appearance + greeting + name only: the fields a public website visitor is allowed to see. */
export function botPublicView(bot) {
  return {
    id: bot.id, tenantId: bot.tenantId, name: bot.name, greeting: bot.greeting,
    appearance: bot.appearance, status: bot.status, leadCapture: leadCaptureFor(bot)
  };
}

export function defaultBotFor(tenantId) {
  const bots = botsStore.all().filter((b) => b.tenantId === tenantId);
  return bots.find((b) => b.status === "active") || bots[0] || null;
}

/** Creates the workspace records a brand-new tenant needs (profile, default bot, key). */
export function provisionTenant(tenant, ownerEmail, businessName) {
  const profiles = profilesStore.all();
  profilesStore.save({
    ...profiles,
    [tenant.id]: {
      businessName: businessName || tenant.name, industry: "", website: "", phone: "", address: "", city: "",
      state: "", country: "", description: "", domain: "", timezone: "", language: "", contactEmail: ownerEmail
    }
  });
  const bots = botsStore.all();
  botsStore.save([
    ...bots,
    makeBot({
      id: genId("bot"), tenantId: tenant.id, name: `${tenant.name.slice(0, 36).trim()} Assistant`, status: "active", model: tenant.plan === "Starter" ? "GPT-4o mini" : "GPT-4o",
      greeting: `Hi! Welcome to ${tenant.name} 👋 How can I help you today?`,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString()
    })
  ]);
}
